// WCAG AA contrast helpers; mirrors the widget's app.helper.ts/widgetTheme.helper.ts (separate codebases).
const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const RGB_RE = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?/;

export const WCAG_AA_NORMAL = 4.5;

type Rgb = [number, number, number];
type Rgba = [number, number, number, number];

const parseColor = (input: string): Rgba | null => {
  if (HEX_RE.test(input)) {
    const hex = input.replace('#', '');
    const expanded =
      hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex;
    const [r, g, b, a = 255] = (expanded.match(/../g) ?? []).map((pair) => parseInt(pair, 16));
    return [r, g, b, a / 255];
  }
  const m = input.match(RGB_RE);
  if (!m) return null;
  // Clamped as CSS clamps them, so `rgba(255, 255, 255, 2)` is judged as the opaque white it renders.
  const clamp = (value: number, max: number) => Math.min(max, Math.max(0, value));
  const alpha = m[4] === undefined ? 1 : Number(m[4]);
  return [
    clamp(Number(m[1]), 255),
    clamp(Number(m[2]), 255),
    clamp(Number(m[3]), 255),
    clamp(alpha, 1),
  ];
};

const luminance = ([r, g, b]: Rgb | Rgba): number => {
  const a = [r, g, b].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
};

const contrast = (first: Rgb | Rgba, second: Rgb | Rgba): number => {
  const [L1, L2] = [luminance(first), luminance(second)];
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
};

export const getContrastRatio = (fg: string, bg: string): number | null => {
  const fgRgb = parseColor(fg);
  const bgRgb = parseColor(bg);
  if (!fgRgb || !bgRgb) return null;
  return contrast(fgRgb, bgRgb);
};

// Black or white, whichever reads better on `bg` (black/white luminance is 0/1).
export const getAccessibleTextColor = (bg: string): string => {
  const rgb = parseColor(bg);
  if (!rgb) return '#000000';
  const L = luminance(rgb);
  const blackContrast = (L + 0.05) / 0.05;
  const whiteContrast = 1.05 / (L + 0.05);
  if (blackContrast >= WCAG_AA_NORMAL) return '#000000';
  if (whiteContrast >= WCAG_AA_NORMAL) return '#ffffff';
  return blackContrast > whiteContrast ? '#000000' : '#ffffff';
};

export const GLASS_OPACITY_MIN = 40;
export const GLASS_OPACITY_MAX = 95;
export const GLASS_OPACITY_STEP = 5;
// A white and a black website, the two extremes the glass may sit over.
const GLASS_PAGES: Rgb[] = [
  [255, 255, 255],
  [0, 0, 0],
];

// The widget's light and dark panels and their text; mirrors the widget's widgetTheme.helper.ts.
export const PRESET_GLASS_COLORS = {
  light: { background: '#ffffff', text: '#030712' },
  dark: { background: '#19191d', text: '#ebebeb' },
} as const;

/** Whether two theme colors are the same, however each is written, alpha included. */
export const isSameColor = (first: string, second: string): boolean => {
  const [a, b] = [parseColor(first), parseColor(second)];
  if (!a || !b) return first.toLowerCase() === second.toLowerCase();
  return a.every((channel, index) => Math.abs(channel - b[index]) < 0.01);
};

/** Text that reads off the glass: directly, or through a translucent `layer` painted on it. */
export type GlassReader = { ink: string; layer?: string };

const composite = (top: Rgba, under: Rgb): Rgb =>
  under.map((channel, index) => channel + (top[index] - channel) * top[3]) as Rgb;

/** Lowest glass opacity with readable text, or null when the palette needs Solid. */
export const getGlassMinimumOpacity = (
  background: string,
  readers: GlassReader[],
): number | null => {
  const panel = parseColor(background);
  const parsed = readers.map(({ ink, layer }) => ({
    ink: parseColor(ink),
    layer: layer === undefined ? undefined : parseColor(layer),
  }));
  if (!panel || parsed.some(({ ink, layer }) => !ink || layer === null)) return null;

  for (
    let percent = GLASS_OPACITY_MIN;
    percent <= GLASS_OPACITY_MAX;
    percent += GLASS_OPACITY_STEP
  ) {
    // The glass percentage scales the panel color's own alpha, as the widget's color-mix() does.
    const glass: Rgba = [panel[0], panel[1], panel[2], (percent / 100) * panel[3]];
    const readable = GLASS_PAGES.every((page) => {
      const tint = composite(glass, page);
      return parsed.every(({ ink, layer }) => {
        const base = layer ? composite(layer, tint) : tint;
        return contrast(composite(ink!, base), base) >= WCAG_AA_NORMAL;
      });
    });
    if (readable) return percent;
  }
  return null;
};

// Structural rather than the dashboard DTOs, so the widget project can import this file for its parity test.
type GlassMode = 'light' | 'dark' | 'custom';
type GlassPalette = Partial<
  Record<'background' | 'header' | 'header_text' | 'ai_bubble_text', string | null>
>;

/** The widget's own floor for a theme: AI replies read off the glass, and so does a header that shows it through. */
export const getModeGlassMinimumOpacity = (
  mode: GlassMode | undefined,
  colors: GlassPalette | undefined,
): number | null => {
  if (mode !== 'custom') {
    const { background, text } = PRESET_GLASS_COLORS[mode === 'dark' ? 'dark' : 'light'];
    return getGlassMinimumOpacity(background, [{ ink: text }]);
  }
  const background = colors?.background ?? PRESET_GLASS_COLORS.light.background;
  const readers: GlassReader[] = [
    { ink: colors?.ai_bubble_text ?? PRESET_GLASS_COLORS.light.text },
  ];
  const title =
    colors?.header_text ??
    (colors?.header ? getAccessibleTextColor(colors.header) : PRESET_GLASS_COLORS.light.text);
  // A header in the panel's own color is see-through; a translucent one tints the glass under its title.
  if (!colors?.header || isSameColor(colors.header, background)) {
    readers.push({ ink: title });
  } else if ((parseColor(colors.header)?.[3] ?? 1) < 1) {
    readers.push({ ink: title, layer: colors.header });
  }
  return getGlassMinimumOpacity(background, readers);
};
